import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { toast } from "react-toast";
import axiosInstance from "../../api/axiosInstance";
import ParameterModal from "./Modals/ParameterModal";

export default function TestProfileData() {
  const { project_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get("profile_id");
  
  const [showParameterModal, setShowParameterModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { register, control, handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      test_profile_name: "",
      sets: [
        {
          set_name: "",
          etf: false,
          data: {}
        }
      ],
      columns: [] // dynamic column list
    }
  });

  const { fields: rowFields, append: addRow, remove: removeRow } = useFieldArray({
    control,
    name: "sets"
  });

  const { fields: colFields, append: addColumn, remove: removeColumn } = useFieldArray({
    control,
    name: "columns"
  });

  const columns = watch("columns");
  const sets = watch("sets");

  // Fetch profile data if in edit mode
  useEffect(() => {
    if (profileId) {
      fetchTestDataProfile();
    }
  }, [profileId, project_id]);

  const fetchTestDataProfile = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/get_test_profile?profile_id=${profileId}`);
      
      if (response.status_code === 200 && response.profile) {
        const { profile, sets: fetchedSets } = response;
        
        // Extract unique columns from sets data
        const columnMap = {};
        fetchedSets.forEach(set => {
          Object.keys(set.data || {}).forEach(key => {
            if (!columnMap[key]) {
              columnMap[key] = { name: key };
            }
          });
        });
        
        const extractedColumns = Object.values(columnMap);
        
        // Prepare sets with proper structure
        const preparedSets = fetchedSets.map(set => ({
          set_name: set.set_name,
          etf: set.etf,
          data: set.data || {}
        }));
        
        // Reset form with fetched data
        reset({
          test_profile_name: profile.test_profile_name,
          sets: preparedSets.length > 0 ? preparedSets : [{ set_name: "", etf: false, data: {} }],
          columns: extractedColumns
        });
        
        setIsEditMode(true);
        toast.success("Profile loaded successfully");
      } else {
        toast.error(response.message || "Failed to load profile");
        setIsEditMode(false);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to load profile";
      toast.error(errorMessage);
      setIsEditMode(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Add Column → also update each row's "data"
  const handleAddColumn = (colName) => {
    if (!colName || colName.trim() === "") {
      toast.error("Parameter name cannot be empty");
      return;
    }

    // Check for duplicate column names
    if (columns.some(col => col.name.toLowerCase() === colName.toLowerCase())) {
      toast.error("Parameter already exists");
      return;
    }

    addColumn({ name: colName });

    // Update each row with empty string
    sets.forEach((_, index) => {
      setValue(`sets.${index}.data.${colName}`, "");
    });

    setShowParameterModal(false);
    toast.success(`Parameter "${colName}" added successfully`);
  };

  const handleDeleteColumn = (index, colName) => {
    removeColumn(index);
    // Remove data from each row
    sets.forEach((_, i) => {
      setValue(`sets.${i}.data.${colName}`, undefined);
    });
  };

  // Add Row → using dynamic columns
  const handleAddRow = () => {
    const newData = {};
    columns.forEach((col) => {
      newData[col.name] = "";
    });

    addRow({
      set_name: "",
      etf: false,
      data: newData
    });
  };

  const onSubmit = async (formData) => {
    // Validation
    if (!formData.test_profile_name || formData.test_profile_name.trim() === "") {
      toast.error("Test profile name is required");
      return;
    }

    if (!formData.sets || formData.sets.length === 0) {
      toast.error("At least one row is required");
      return;
    }

    // Check if all set names are filled
    const emptySetName = formData.sets.some(set => !set.set_name || set.set_name.trim() === "");
    if (emptySetName) {
      toast.error("Set name is mandatory for all rows");
      return;
    }

    const payload = {
      project_id: parseInt(project_id),
      test_profile_name: formData.test_profile_name,
      sets: formData.sets.map((row) => ({
        set_name: row.set_name,
        etf: row.etf,
        data: row.data
      }))
    };

    // Add profile_id if in edit mode
    if (isEditMode && profileId) {
      payload.profile_id = parseInt(profileId);
    }

    setIsSubmitting(true);

    try {
      let response;
      
      if (isEditMode && profileId) {
        // Update profile
        response = await axiosInstance.put(
          "/update_test_profile",
          payload
        );
      } else {
        // Create new profile
        response = await axiosInstance.post(
          "/create_test_profile",
          payload
        );
      }

      if (response.status_code === 201) {
        // const successMessage = isEditMode 
        //   ? "Test profile updated successfully" 
        //   : "Test profile created successfully";
        // toast.success(successMessage);
        navigate(`/dashboard/test-data-list/${project_id}`);
      } else {
        toast.error(response.message || "Failed to save test profile");
      }
    } catch (error) {
      console.error("API Error:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save test profile";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">
            {isEditMode ? "Edit Test Profile Data" : "Test Profile Data"}
          </h1>
          <p className="text-slate-600">
            {isEditMode ? "Update test data parameters" : "Create and manage test data parameters"}
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white rounded-lg shadow-lg p-8 flex items-center justify-center">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-slate-600">Loading profile data...</p>
            </div>
          </div>
        )}

        {/* Form Container */}
        {!isLoading && (
          <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* Profile Name */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Test Profile Name <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("test_profile_name")}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter Test Profile Name"
                />
              </div>

              {/* Table Section */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-slate-900">Parameters</h2>
                  {/* Add Parameter Icon Button */}
                  <button
                    type="button"
                    onClick={() => setShowParameterModal(true)}
                    className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
                    title="Add Parameter"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full">
                    <thead className="bg-slate-100 border-b border-slate-300">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Set Name</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">ETF</th>

                        {columns.map((col, index) => (
                          <th key={index} className="px-4 py-3 text-left text-sm font-semibold text-slate-700">
                            <div className="flex items-center justify-between gap-2">
                              <span>{col.name}</span>
                              <button
                                type="button"
                                onClick={() => handleDeleteColumn(index, col.name)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition-colors"
                                title="Delete parameter"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </th>
                        ))}

                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {rowFields.map((row, rowIndex) => (
                        <tr key={row.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3">
                            <input
                              {...register(`sets.${rowIndex}.set_name`)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                              placeholder="Set Name"
                            />
                          </td>

                          {/* ETF Toggle */}
                          <td className="px-4 py-3 text-center">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                {...register(`sets.${rowIndex}.etf`)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-slate-300 peer-focus:ring-2 peer-focus:ring-blue-500
                                  rounded-full peer peer-checked:bg-blue-600 transition-colors"></div>
                              <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full
                                  peer-checked:translate-x-5 transition-transform"></span>
                            </label>
                          </td>

                          {/* Dynamic fields */}
                          {columns.map((col, colIndex) => (
                            <td key={colIndex} className="px-4 py-3">
                              <input
                                {...register(`sets.${rowIndex}.data.${col.name}`)}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                placeholder={col.name}
                              />
                            </td>
                          ))}

                          {/* Delete Row */}
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => removeRow(rowIndex)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-2 transition-colors"
                              title="Delete row"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {rowFields.length === 0 && (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-300 mt-4">
                    <p className="text-slate-600 text-sm">No rows added yet. Click the + icon below to add your first row.</p>
                  </div>
                )}
              </div>

              {/* Bottom Action Buttons */}
              <div className="flex items-center justify-between pt-6 border-t border-slate-200">
                {/* Add Row Button - Left */}
                <button
                  type="button"
                  onClick={handleAddRow}
                  className="flex items-center gap-2 p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
                  title="Add Row"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-sm font-medium">Add Row</span>
                </button>

                {/* Save/Update Button - Right */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-lg hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update Profile" : "Save Profile")}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Parameter Modal */}
      <ParameterModal
        isOpen={showParameterModal}
        onClose={() => setShowParameterModal(false)}
        onSave={handleAddColumn}
      />
    </div>
  );
}
